import classnames from 'classnames';
import * as React from 'react';

import { Icon } from '@ao-internal/components-react';

import { getPercent } from '../helpers/math';
import { ComponentWithAs } from '../types';

export interface ProductSummaryModel {
	productId?: number;
	productCode: string;
	title: string;
	url: string;
	imageUrl: string;
	isForSale: boolean;
	isInStock?: boolean;
	price: number;
	retailPrice?: number;
	savingsAmount?: number;
	formattedPrice: string;
	formattedRetailPrice?: string;
	formattedSavingsAmount?: string;
	review?: { averageRating: number; totalReviewCount: number };
	brand: string;
	category: string;
	fittingName: string;
}

interface ProductSummaryProps {
	taggingPrefix: string;
	product: ProductSummaryModel;
}

const DEFAULT_ELEMENT = 'div';

export const ProductSummary = React.forwardRef(function ProductSummary(
	{
		taggingPrefix,
		product,
		as: ElementType = DEFAULT_ELEMENT,
		className,
		...props
	},
	ref
) {
	return (
		<ElementType
			data-testid={`${taggingPrefix}-${product.productCode}`}
			className={classnames('flex p-3 font-regular', className)}
			{...props}
			ref={ref}
		>
			<div>
				<img
					className="block"
					style={{ maxWidth: '72px' }}
					src={product.imageUrl}
					alt=""
				/>
			</div>
			<div className="flex-grow pl-4">
				<div>
					<strong className="line-clamp-2 text-title-sm">
						{product.title}
					</strong>
				</div>
				{product.review && product.review.totalReviewCount > 0 && (
					<div
						data-testid={`${taggingPrefix}-review`}
						className="text-body-xs mt-2 leading-none"
					>
						<span className="sr-only">
							<span data-testid={`${taggingPrefix}-review-rating`}>
								{product.review.averageRating}
							</span>{' '}
							out of 5 stars
						</span>
						<div className="relative inline-block align-middle">
							<div
								className="icon-starrating-inverted text-rating absolute z-20 overflow-hidden"
								style={{
									width: `${getPercent(product.review.averageRating, 5)}%`,
								}}
							></div>
							<div className="icon-starrating-inverted text-gray-60 relative z-10"></div>
						</div>
						<div
							data-testid={`${taggingPrefix}-review-average-rating`}
							className="inline-block pl-1 align-middle"
							aria-hidden
						>
							{product.review.averageRating}/5
						</div>
						<div
							data-testid={`${taggingPrefix}-review-count`}
							className="inline-block pl-1 align-middle"
						>
							({product.review.totalReviewCount} Reviews)
						</div>
					</div>
				)}
				<div className="flex items-end mt-1">
					<strong
						data-testid={`${taggingPrefix}-price`}
						className={classnames('text-display-sm', {
							'text-deals': !!product.savingsAmount,
						})}
					>
						{product.formattedPrice}
					</strong>

					{!!product.savingsAmount && (
						<span
							data-testid={`${taggingPrefix}-savings`}
							className="text-caption pl-1"
						>
							Save {product.formattedSavingsAmount}
						</span>
					)}

					{!!product.retailPrice && (
						<span
							data-testid={`${taggingPrefix}-rrp`}
							className="text-caption pl-2"
						>
							RRP {product.formattedRetailPrice}
						</span>
					)}
				</div>

				{product.isInStock === false && (
					<div
						data-testid={`${taggingPrefix}-out-of-stock`}
						className="text-title-sm bg-bread inline-flex items-center p-1 mt-2 rounded"
					>
						<Icon className="text-error mr-1" icon="clock"></Icon>
						<span>Back in stock soon</span>
					</div>
				)}
			</div>
		</ElementType>
	);
}) as ComponentWithAs<ProductSummaryProps, typeof DEFAULT_ELEMENT>;

export default ProductSummary;
